import type {DateRangeValue} from '../types';

import {RangeCalendar} from '@date-picker';
import {BottomSheet, Button, Loading} from '@ds/components';
import {toast} from '@Toast';
import {Fragment, useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {confirmDeal, getDealSummary} from 'store/slices/custom-deal';

import {CUSTOM_DEAL_ROUTES} from '../constants';
import {getCustomDealVendorId} from '../utils';

const SelectDate: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {dealSummary, loading} = useAppSelector((state) => state.customDeal);
  const [showDateModal, setShowDateModal] = useState(false);
  const [dateRange, setDateRange] = useState<DateRangeValue>({start: undefined, end: undefined});
  const vendorId = getCustomDealVendorId();
  const {dealId} = useParams();

  useEffect(() => {
    if (dealId) {
      void dispatch(getDealSummary({vendorId, dealId}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  const categories = useMemo(
    () =>
      ((dealSummary || []) as Array<{
        parent_category_title?: string;
        category_title?: string;
        product_count?: number;
      }>).map((item, index) => ({
        id: index,
        title:
          item.parent_category_title && item.category_title
            ? `${item.parent_category_title}/${item.category_title}`
            : item.category_title || item.parent_category_title || '',
        count: item.product_count || 0
      })),
    [dealSummary]
  );

  const handleConfirm = async () => {
    if (!dateRange.start || !dateRange.end) {
      return;
    }

    const currentTime = new Date().toISOString().split('T')[1];
    const startDateTime = `${dateRange.start}T${currentTime}`;
    const endDateTime = `${dateRange.end}T${currentTime}`;

    try {
      const response = (await dispatch(
        confirmDeal({
          vendorId,
          startDate: startDateTime,
          endDate: endDateTime
        })
      ).unwrap()) as {errors?: {message?: string}};

      if (response?.errors?.message) {
        toast(response.errors.message);
        return;
      }

      toast('تخفیف فروشگاه شما با موفقیت ایجاد شد');
      setShowDateModal(false);
      navigate(CUSTOM_DEAL_ROUTES.list);
    } catch (error: unknown) {
      const requestError = error as {errors?: {message?: string}; message?: string};
      toast(
        requestError.errors?.message ||
          requestError.message ||
          'خطایی در برقراری ارتباط با سرور رخ داد'
      );
    }
  };

  if (loading && !categories.length) {
    return (
      <div className="flex min-h-60 flex-1 items-center justify-center">
        <Loading size="large" color="primary" />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white px-4">
      <div className="py-4 text-body-sm-emphasis text-neutral-fg">دسته‌بندی ها</div>
      <div className="flex min-h-0 flex-col overflow-y-auto">
        <section className="flex flex-col">
          {categories.map((category, index) => (
            <Fragment key={category.id}>
              {index > 0 ? <div className="h-px bg-gray-02" /> : null}
              <div className="flex h-16 items-center gap-2 py-3">
                <span className="text-body-sm text-neutral-fg">{category.title}</span>
                <span className="text-body-sm text-gray-08">
                  ({category.count.toLocaleString('fa-IR')} کالا)
                </span>
              </div>
              <div className="h-px bg-gray-02" />
            </Fragment>
          ))}
        </section>
      </div>
      <div className="shrink-0 py-4">
        <div className="flex h-16 shrink-0 flex-col py-1.5">
          <span className="text-body-sm text-gray-07">نوع تخفیف</span>
          <span className="text-body-sm-emphasis text-neutral-fg">تخفیف سوپرمارکت شما</span>
        </div>
        <Button size="lg" fullWidth onClick={() => setShowDateModal(true)}>
          انتخاب زمان
        </Button>
      </div>
      <BottomSheet
        show={showDateModal}
        onClose={() => setShowDateModal(false)}
        contentStyle={{padding: '24px 16px', maxHeight: '90vh'}}
        header={
          <div className="flex flex-col items-center px-4 py-2 text-subtitle-md text-neutral-fg">
            <span className="-mt-6 mb-4 h-1 w-8 rounded-full bg-gray-03" aria-hidden />
            <span>انتخاب زمان</span>
          </div>
        }
        canceledOnTouchOutside
      >
        <div className="flex max-h-[calc(90vh-120px)] w-full flex-col gap-6 overflow-y-auto">
          <RangeCalendar disableToday value={dateRange} onChange={setDateRange} />
          <div className="mt-auto flex w-full flex-col gap-2">
            <Button
              fullWidth
              size="lg"
              onClick={() => void handleConfirm()}
              disabled={!dateRange.start || !dateRange.end}
            >
              تایید
            </Button>
            <Button variant="ghost" size="lg" fullWidth onClick={() => setShowDateModal(false)}>
              بستن
            </Button>
          </div>
        </div>
      </BottomSheet>
    </div>
  );
};

export default SelectDate;
